import { useEffect, useRef } from "react";

/**
 * Animated neural-network background.
 *
 *   - Soft drifting nodes connected by fading lines whose opacity scales
 *     with proximity² so close connections light up, distant ones whisper.
 *   - Periodic "firings": a bright accent dot travels along a random close
 *     connection, leaving a brief glow trail. Spawns ~one every 1.4 s.
 *   - Cursor warps the field gently — nodes within ~180 px lean toward
 *     the cursor by a few pixels with decay (no jarring magnet effect).
 *   - Halo glow around each node (subtle, via radial gradient fill).
 *
 *   - Honours prefers-reduced-motion (single frame, then stops; no pulses,
 *     no cursor warp).
 *   - Pauses while the tab is hidden.
 *   - Crisp on retina via devicePixelRatio.
 *   - Particle count scales with viewport area.
 */
export function NeuralBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const accent = readCssColor("--color-accent") ?? { r: 184, g: 145, b: 90 };

    type P = { x: number; y: number; vx: number; vy: number; r: number };
    type Pulse = { from: number; to: number; t: number; speed: number };
    let particles: P[] = [];
    let pulses: Pulse[] = [];
    let width = 0;
    let height = 0;
    const linkDist = 170;
    const baseAlpha = 0.55;
    const mouse = { x: -9999, y: -9999, active: false };

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const area = width * height;
      const target = Math.max(20, Math.min(72, Math.floor(area / 30000)));
      particles = Array.from({ length: target }, () => spawn(width, height));
    };

    const spawnPulse = () => {
      if (particles.length < 2) return;
      // Pick a pair within link distance.
      for (let tries = 0; tries < 8; tries++) {
        const i = Math.floor(Math.random() * particles.length);
        const j = Math.floor(Math.random() * particles.length);
        if (i === j) continue;
        const a = particles[i];
        const b = particles[j];
        if (!a || !b) continue;
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (dist > linkDist) continue;
        pulses.push({ from: i, to: j, t: 0, speed: 0.012 + Math.random() * 0.012 });
        return;
      }
    };

    let lastPulseAt = 0;

    const step = (now: number) => {
      ctx.clearRect(0, 0, width, height);

      // Drift particles, with subtle cursor attraction.
      for (const p of particles) {
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 180 * 180 && d2 > 1) {
            const d = Math.sqrt(d2);
            const f = (1 - d / 180) * 0.06;
            p.vx += (dx / d) * f * 0.2;
            p.vy += (dy / d) * f * 0.2;
          }
        }
        // Damping so cursor pushes don't accumulate.
        p.vx *= 0.985;
        p.vy *= 0.985;
        // Minimum drift so things never freeze.
        if (Math.abs(p.vx) < 0.02) p.vx += (Math.random() - 0.5) * 0.04;
        if (Math.abs(p.vy) < 0.02) p.vy += (Math.random() - 0.5) * 0.04;
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }

      // Connections.
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          if (!a || !b) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist > linkDist) continue;
          const t = 1 - dist / linkDist;
          ctx.strokeStyle = `rgba(${accent.r}, ${accent.g}, ${accent.b}, ${t * t * baseAlpha * 0.55})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Nodes (with subtle halo).
      for (const p of particles) {
        const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 6);
        halo.addColorStop(0, `rgba(${accent.r}, ${accent.g}, ${accent.b}, ${baseAlpha * 0.6})`);
        halo.addColorStop(1, `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0)`);
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${accent.r}, ${accent.g}, ${accent.b}, ${baseAlpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Pulses — bright dot travelling along a connection.
      const nextPulses: Pulse[] = [];
      for (const pulse of pulses) {
        const a = particles[pulse.from];
        const b = particles[pulse.to];
        if (!a || !b) continue;
        pulse.t += pulse.speed;
        if (pulse.t >= 1) continue;
        const x = a.x + (b.x - a.x) * pulse.t;
        const y = a.y + (b.y - a.y) * pulse.t;
        // Glow.
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 14);
        glow.addColorStop(0, `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0.85)`);
        glow.addColorStop(1, `rgba(${accent.r}, ${accent.g}, ${accent.b}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, 14, 0, Math.PI * 2);
        ctx.fill();
        // Core.
        ctx.fillStyle = `rgba(${accent.r + 30}, ${accent.g + 30}, ${accent.b + 30}, 0.95)`;
        ctx.beginPath();
        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
        ctx.fill();
        nextPulses.push(pulse);
      }
      pulses = nextPulses;

      // Spawn ~one pulse per 1.4s.
      if (!reduced && now - lastPulseAt > 1400 && pulses.length < 6) {
        spawnPulse();
        lastPulseAt = now;
      }
    };

    let rafId = 0;
    let running = true;
    const loop = (time: number) => {
      if (!running) return;
      step(time);
      rafId = requestAnimationFrame(loop);
    };

    const onVisibility = () => {
      const wasRunning = running;
      running = !document.hidden && !reduced;
      if (running && !wasRunning) {
        rafId = requestAnimationFrame(loop);
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };
    const onMouseLeave = () => {
      mouse.active = false;
    };

    resize();
    if (reduced) {
      step(0);
    } else {
      rafId = requestAnimationFrame(loop);
    }
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    if (!reduced) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseleave", onMouseLeave);
    }
    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 h-full w-full opacity-[0.5]"
    />
  );
}

function spawn(width: number, height: number) {
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.14,
    vy: (Math.random() - 0.5) * 0.14,
    r: 0.9 + Math.random() * 1.1,
  };
}

function readCssColor(varName: string): { r: number; g: number; b: number } | null {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (!raw) return null;
  if (raw.startsWith("#") && raw.length === 7) {
    return {
      r: parseInt(raw.slice(1, 3), 16),
      g: parseInt(raw.slice(3, 5), 16),
      b: parseInt(raw.slice(5, 7), 16),
    };
  }
  const m = raw.match(/rgb\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (m && m[1] && m[2] && m[3]) {
    return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) };
  }
  return null;
}
